import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import http from "../api/http";
import IdentityProvider from "../types/identityProvider";
import Provider from "../types/provider";
import { logout } from "./auth";

interface ProviderState {
  status: "idle" | "loading" | "failed";
  hasFetchedProvider: boolean;
  ssoStatus?: {
    domainName?: string;
    useSso?: boolean;
  };
  provider?: Provider;
}

const initialState: ProviderState = {
  status: "idle",
  hasFetchedProvider: false,
};

export const checkDomain = createAsyncThunk<string, string>(
  "provider/domain",
  async (domain: string) => {
    const response = await http.get<string>(`/provider/name?domain=${domain}`);
    return response.data;
  }
);

export const registerProvider = createAsyncThunk<boolean, IdentityProvider>(
  "provider/register",
  async (ssoSettings: IdentityProvider) => {
    const response = await http.post<boolean>(`/provider`, ssoSettings);
    return response.data;
  }
);

export const getProvider = createAsyncThunk<Provider>(
  "provider/get",
  async () => {
    const response = await http.get<Provider>(`/provider`, {
      params: {
        refreshToken: true,
      },
    });
    return response.data;
  }
);

export const updateProvider = createAsyncThunk<null, IdentityProvider>(
  "provider/update",
  async (ssoSettings: IdentityProvider) => {
    const response = await http.put(`/provider`, ssoSettings);
    return response.data;
  }
);

const providerSlice = createSlice({
  name: "provider",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(checkDomain.pending, (state) => {
        state.status = "loading";
      })
      .addCase(checkDomain.fulfilled, (state, action) => {
        state.status = "idle";
        state.ssoStatus = {
          domainName: action.payload ?? "",
          useSso: action.payload === "" ? false : true,
        };
      })
      .addCase(checkDomain.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(getProvider.pending, (state) => {
        state.status = "loading";
        state.hasFetchedProvider = true;
      })
      .addCase(getProvider.fulfilled, (state, action) => {
        state.status = "idle";
        state.provider = action.payload;

        if (action.payload) {
          state.ssoStatus = {
            domainName: action.payload.providerName,
            useSso: true,
          };
          action.payload.logoutUrl
            ? sessionStorage.setItem("logoutUrl", action.payload.logoutUrl)
            : sessionStorage.removeItem("logoutUrl");
        } else {
          state.ssoStatus = initialState.ssoStatus;
        }
      })
      .addCase(getProvider.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(logout.fulfilled, () => {
        return { ...initialState };
      });
  },
});

export default providerSlice.reducer;
